import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { SearchIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";

import {
  AlertKind,
  apiPutAlertsMarkAllSeenPatientId,
  apiPutAlertsMarkSeenUuid,
  DefaultPatientAlertResponse,
} from "../../api";
import { alertTypes } from "../../constants/enum-to-text";
import { DataModel } from "../../models/data-model";
import { floatRounding } from "../../utils/utils";
import { Button } from "../ui-kit/button";
import { resolveEnumLabel } from "../ui-kit/fields/utils";
import { PopoverContent } from "../ui-kit/popover";
import { type Column, DataTable } from "../ui-kit/tables/data-table";
import styles from "./cell-nested-alerts.module.css";

export const CellNestedAlerts = observer(function CellNestedAlerts({
  alerts,
  filterable = false,
  markAllSeenFor,
}: {
  alerts: DefaultPatientAlertResponse[];
  /** Show the DataTable filter toolbar (opt-in; off for the waiting rooms). */
  filterable?: boolean;
  /**
   * Patient whose alerts the bulk action marks seen. Opt-in: pass an id to show
   * the action, omit it to hide it (the waiting rooms do). The action covers
   * *all* of that patient's alerts server-side, not just the rows listed here.
   */
  markAllSeenFor?: number;
}) {
  const [open, setOpen] = useState(false);

  if (alerts.length === 0) {
    return <>0</>;
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        className={`${styles.trigger} items-center text-brand-primary`}
      >
        <span className="mr-1">{alerts.length}</span>
        <SearchIcon size={12} />
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverContent>
          <AlertsTable
            alerts={alerts}
            filterable={filterable}
            markAllSeenFor={markAllSeenFor}
            onEmpty={() => setOpen(false)}
          />
        </PopoverContent>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});

const AlertsTable = observer(function AlertsTable({
  alerts,
  filterable,
  markAllSeenFor,
  onEmpty,
}: {
  alerts: DefaultPatientAlertResponse[];
  filterable: boolean;
  markAllSeenFor: number | undefined;
  onEmpty: () => void;
}) {
  const { t } = useLingui();
  // Filters are grouped by string equality, so the group name is defined once.
  const filterGroup = t`Alert`;
  const [model] = useState(() => {
    const m = new DataModel(alerts);
    m.init();
    return m;
  });

  const columns: Column<DefaultPatientAlertResponse>[] = [
    {
      header: <Trans>Date</Trans>,
      attr: "alert_timestamp",
      type: "date",
      sort: true,
      initSort: "desc",
    },
    {
      header: <Trans>Variable</Trans>,
      attr: "alert_variable",
      type: "string",
      filter: filterable
        ? { kind: "text", group: filterGroup, placeholder: t`Search…` }
        : undefined,
      cell: ({ item }) => (
        <>
          <strong>{item.alert_variable}</strong>:{" "}
          {floatRounding(item.alert_value, 1)}
        </>
      ),
    },
    {
      header: <Trans>Reason</Trans>,
      attr: "alert_reason",
      type: "string",
      filter: filterable
        ? { kind: "text", group: filterGroup, placeholder: t`Search…` }
        : undefined,
      cell: ({ item }) => (
        <>
          {resolveEnumLabel(alertTypes[item.alert_type])}: {item.alert_reason}
        </>
      ),
    },
    {
      header: <Trans>Seen</Trans>,
      attr: "alert_seen",
      type: "boolean",
      filter: filterable
        ? {
            kind: "bool",
            label: t`Unseen only`,
            group: filterGroup,
            predicate: (item) => !item.alert_seen,
          }
        : undefined,
    },
    {
      header: <Trans>Mark seen</Trans>,
      cell: ({ item }) => (
        <Button
          onClick={async () => {
            await apiPutAlertsMarkSeenUuid({ path: { uuid: item.uuid } });
            removeAlert(alerts, item.uuid, model, onEmpty);
          }}
        >
          <Trans>Seen</Trans>
        </Button>
      ),
    },
  ];

  return (
    <DataTable
      model={model}
      itemKeyField="uuid"
      columns={columns}
      rowsPerPage={6}
      filterNoun={
        filterable
          ? (count) => t`${plural(count, { one: "alert", other: "alerts" })}`
          : undefined
      }
      filterActions={
        // Deliberately unaffected by the table's filter: the endpoint is
        // per-patient and marks every alert kind, so the label says so rather
        // than implying it only covers the rows currently listed.
        markAllSeenFor != null ? (
          <Button
            variant="subtle"
            size="sm"
            data-testid="cell-alerts-mark-all-seen"
            title={t`Marks every alert of this patient as seen, including any not listed here`}
            onClick={async () => {
              await apiPutAlertsMarkAllSeenPatientId({
                path: { patient_id: markAllSeenFor },
                query: {
                  alert_kinds: [
                    AlertKind.medical,
                    AlertKind.technical,
                    AlertKind.compliance,
                  ],
                },
              });
              alerts.splice(0, alerts.length);
              onEmpty();
            }}
          >
            <Trans>Mark all patient alerts seen</Trans>
          </Button>
        ) : undefined
      }
    />
  );
});

/** Drop a marked-seen alert from the shared array + table, closing when empty. */
function removeAlert(
  alerts: DefaultPatientAlertResponse[],
  uuid: string,
  model: DataModel<DefaultPatientAlertResponse>,
  onEmpty: () => void,
) {
  const index = alerts.findIndex((i) => i.uuid === uuid);
  if (index !== -1) {
    alerts.splice(index, 1);
  }
  if (alerts.length === 0) {
    onEmpty();
  } else {
    model.updateData(alerts);
  }
}
